(() => {
  if (customElements.get('sissilux-pdp-navigation')) return;
  class PDPQuickNavigation extends HTMLElement {
    connectedCallback() {
      if(this.mobileQuery)return;
      this.mobileQuery=matchMedia('(max-width: 749px)');
      this.onViewportChange=()=>{if(this.mobileQuery.matches)this.start();else this.stop();};
      this.mobileQuery.addEventListener('change',this.onViewportChange);
      this.onViewportChange();
    }
    start() {
      this.nav = this.querySelector('nav');
      if (!this.nav || this.abort) return;
      this.abort = new AbortController();
      this.links = [...this.nav.querySelectorAll('[data-destination]')];
      this.targets = {};
      this.needsResolve = true;
      this.schedule = () => {
        if (this.frame || this.updateTimer) return;
        const queue = () => {
          this.updateTimer = 0;
          this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.lastUpdate = performance.now();
            this.update();
          });
        };
        const wait = 80 - (performance.now() - (this.lastUpdate || 0));
        if (wait > 0) this.updateTimer = setTimeout(queue, wait);
        else queue();
      };
      const options = { signal: this.abort.signal, passive: true };
      window.addEventListener('scroll', this.schedule, options);
      const refresh = () => { this.needsResolve = true; this.schedule(); };
      window.addEventListener('resize', refresh, options);
      window.visualViewport?.addEventListener('resize', this.schedule, options);
      document.addEventListener('shopify:section:load', refresh, options);
      document.addEventListener('shopify:section:unload', refresh, options);
      this.nav.addEventListener('click', event => this.navigate(event), { signal: this.abort.signal });
      this.observer = new MutationObserver(records => {
        const external = records.filter(record => !this.contains(record.target));
        if (!external.length) return;
        if (external.some(record => record.type === 'childList' || record.attributeName === 'hidden')) this.needsResolve = true;
        this.schedule();
      });
      this.observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open', 'class'] });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(document.querySelector('main') || document.body);
      this.schedule();
    }
    disconnectedCallback() {
      this.mobileQuery?.removeEventListener('change',this.onViewportChange);
      this.mobileQuery=null;
      this.stop();
    }
    stop() {
      const nav=this.querySelector('nav');if(nav)nav.hidden=true;
      this.abort?.abort();
      this.abort = null;
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.focusTimer);
      clearTimeout(this.updateTimer);
      this.updateTimer = 0;
      this.frame = 0;
    }
    visible(element) {
      return Boolean(element?.isConnected && !element.closest('[hidden]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    }
    configured(value) {
      if (!value?.trim()) return null;
      const key = value.trim().replace(/^#/, '');
      const exact = document.getElementById(key) || document.getElementById('shopify-section-' + key);
      if (exact) return exact;
      // Section keys remain stable even when Shopify changes the template prefix.
      const section = [...document.querySelectorAll('[id^="shopify-section-"]')].find(node => node.id.endsWith('__' + key));
      if (section) return section;
      try { return document.querySelector(value); } catch { return null; }
    }
    first(selectors, root = document) {
      for (const selector of selectors) {
        const found = [...root.querySelectorAll(selector)].find(node => this.visible(node));
        if (found) return found;
      }
      return null;
    }
    resolve() {
      this.main = document.querySelector('[id^="MainProduct-"]');
      this.atc = this.main && this.first(['button[id^="ProductSubmitButton-"]', 'button[name="add"]'], this.main);
      const automatic = {
        reviews: () => this.first(['[data-pdp-reviews]', '#judgeme_product_reviews', '.jdgm-review-widget', '[data-sdiv3-support]', '[data-sdiv2-support]']),
        faq: () => this.first(['[data-pdp-faq]', 'sissilux-faq']),
        buy: () => this.main && (this.first(['sissilux-quantity-offers', '.product-form__quantity', 'product-form'], this.main) || this.atc)
      };
      this.links.forEach(link => {
        const key = link.dataset.destination;
        const node = this.dataset[key]?.trim() ? this.configured(this.dataset[key]) : automatic[key]();
        const target = this.visible(node) ? node : null;
        this.targets[key] = target;
        if (link.hidden === Boolean(target)) link.hidden = !target;
        if (target) {
          if (!target.id) target.id = 'pdp-quick-' + key;
          if (link.getAttribute('href') !== '#' + target.id) link.setAttribute('href', '#' + target.id);
          link.removeAttribute('aria-disabled');
          link.removeAttribute('title');
        } else {
          link.removeAttribute('href');
          link.removeAttribute('aria-disabled');
          link.removeAttribute('title');
          link.removeAttribute('aria-current');
        }
      });
      this.footer = this.first(['.shopify-section-group-footer-group', 'footer']);
      this.header = document.querySelector('sticky-header') || document.querySelector('.section-header');
      this.sticky = this.first(['sticky-atc', '.sticky-atc']);
      this.needsResolve = false;
    }
    headerOffset(reserve = false) {
      const rect = this.header?.getBoundingClientRect();
      if (reserve && rect) return Math.min(rect.height, innerHeight / 3);
      return rect && rect.top <= 0 && rect.bottom > 0 ? Math.min(rect.bottom, innerHeight / 3) : 0;
    }
    update() {
      if(!this.abort||!this.mobileQuery?.matches)return;
      if (this.needsResolve || !this.atc?.isConnected) this.resolve();
      // Read geometry together; only write presentation after these reads.
      const header = this.headerOffset();
      const footerRect = this.footer?.getBoundingClientRect();
      const atcRect = this.atc?.getBoundingClientRect();
      const overlay = document.querySelector('#Details-menu-drawer-container[open], cart-drawer.active, dialog[open], modal-dialog[open], .sdiv3-review-text-dialog[open], .sdiv3-review-dialog[open]');
      const hidden = !atcRect || atcRect.bottom > header ||
        Boolean(footerRect && footerRect.top < innerHeight && footerRect.bottom > 0) || Boolean(overlay);
      let bottom, current;
      if (!hidden) {
        const stickyRect = this.sticky?.getBoundingClientRect();
        bottom = stickyRect && stickyRect.top < innerHeight && stickyRect.bottom > 0 ? innerHeight - Math.max(0, stickyRect.top) + 12 : 12;
        const line = header + Math.min(180, innerHeight * .2);
        let latest = -Infinity;
        for (const link of this.links) {
          const target = this.targets[link.dataset.destination];
          if (!target) continue;
          const rect = target.getBoundingClientRect();
          // Highlight the region being read, not a destination already passed.
          if (rect.top <= line && rect.bottom > line && rect.top >= latest) { latest = rect.top; current = link; }
        }
      }
      if (this.nav.hidden !== hidden) this.nav.hidden = hidden;
      if (hidden) return;
      if (this.lastBottom !== bottom) {
        this.nav.style.bottom = 'calc(' + bottom + 'px + env(safe-area-inset-bottom, 0px))';
        this.lastBottom = bottom;
      }
      if (this.current !== current) {
        this.links.forEach(link => {
          if (link === current) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        this.current = current;
      }
    }
    navigate(event) {
      const link = event.target.closest('a[data-destination]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.resolve();
      const target = this.targets[link.dataset.destination];
      if (!target) return;
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const top = Math.max(0, scrollY + target.getBoundingClientRect().top - this.headerOffset(true) - Number(this.dataset.offset || 16));
      window.scrollTo({ top, behavior: reduced ? 'instant' : 'smooth' });
      clearTimeout(this.focusTimer);
      // Focus a heading/control, not the full-width section (theme focus styles draw a large outline).
      if (event.detail === 0) this.focusTimer = setTimeout(() => {
        if (!target.isConnected) return;
        const focusTarget = target.querySelector('h2, h3, button, input, a[href]') || target;
        const temporary = !focusTarget.hasAttribute('tabindex');
        if (temporary) focusTarget.setAttribute('tabindex', '-1');
        focusTarget.classList.add('pdp-quick-nav-focus');
        focusTarget.addEventListener('blur', () => {
          if (temporary) focusTarget.removeAttribute('tabindex');
          focusTarget.classList.remove('pdp-quick-nav-focus');
        }, { once: true });
        focusTarget.focus({ preventScroll: true });
      }, reduced ? 0 : 700);
    }
  }
  customElements.define('sissilux-pdp-navigation', PDPQuickNavigation);
})();
